import { ref, computed } from 'vue'
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  OAuthProvider,
  updateProfile
} from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { auth, db } from '../firebase'
import { useAnalytics } from './useAnalytics'

const { identifyUser, resetUser } = useAnalytics()

const user = ref(null)
const userProfile = ref(null)
const isLoading = ref(true)
const error = ref(null)

const isLoggedIn = computed(() => !!user.value)

const getErrorMessage = (errorCode) => {
  const messages = {
    'auth/email-already-in-use': 'This email is already registered. Try signing in instead.',
    'auth/wrong-password': 'Incorrect password. Try again or reset your password.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/weak-password': 'Password should be at least 6 characters.',
    'auth/network-request-failed': 'Network error. Check your connection.',
    'auth/popup-closed-by-user': null,
    'auth/cancelled-popup-request': null,
    'auth/invalid-credential': 'Invalid credentials. Please try again.'
  }
  return messages[errorCode] || 'An error occurred. Please try again.'
}

const fetchUserProfile = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid)
    const userDoc = await getDoc(userRef)
    if (userDoc.exists()) {
      userProfile.value = userDoc.data()
    } else {
      // Create user document on first login so security rules can reference it
      const initialProfile = { createdAt: new Date() }
      await setDoc(userRef, initialProfile, { merge: true })
      userProfile.value = initialProfile
    }
  } catch (err) {
    console.error('Error fetching user profile:', err)
  }
}

// Initialize auth listener immediately (singleton pattern)
onAuthStateChanged(auth, async (firebaseUser) => {
  user.value = firebaseUser
  if (firebaseUser) {
    await fetchUserProfile(firebaseUser.uid)
    identifyUser(firebaseUser, userProfile.value || {})
  } else {
    userProfile.value = null
    resetUser()
  }
  isLoading.value = false
})

const signInWithGoogle = async () => {
  error.value = null
  try {
    const provider = new GoogleAuthProvider()
    await signInWithPopup(auth, provider)
    return { success: true }
  } catch (err) {
    const message = getErrorMessage(err.code)
    if (message) {
      error.value = message
      return { success: false, error: message }
    }
    return { success: false, error: null }
  }
}

const signInWithApple = async () => {
  error.value = null
  try {
    const provider = new OAuthProvider('apple.com')
    provider.addScope('email')
    provider.addScope('name')
    await signInWithPopup(auth, provider)
    return { success: true }
  } catch (err) {
    const message = getErrorMessage(err.code)
    if (message) {
      error.value = message
      return { success: false, error: message }
    }
    return { success: false, error: null }
  }
}

const signInWithEmail = async (email, password) => {
  error.value = null
  try {
    await signInWithEmailAndPassword(auth, email, password)
    return { success: true }
  } catch (err) {
    const message = getErrorMessage(err.code)
    error.value = message
    return { success: false, error: message }
  }
}

const createAccountWithEmail = async (email, password, displayName) => {
  error.value = null
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    if (displayName) {
      await updateProfile(userCredential.user, { displayName })
    }
    return { success: true }
  } catch (err) {
    const message = getErrorMessage(err.code)
    error.value = message
    return { success: false, error: message }
  }
}

const sendPasswordReset = async (email) => {
  error.value = null
  try {
    await sendPasswordResetEmail(auth, email)
    return { success: true }
  } catch (err) {
    const message = getErrorMessage(err.code)
    error.value = message
    return { success: false, error: message }
  }
}

const signOut = async () => {
  error.value = null
  try {
    await firebaseSignOut(auth)
    user.value = null
    userProfile.value = null
    return { success: true }
  } catch (err) {
    error.value = 'Failed to sign out. Please try again.'
    return { success: false, error: error.value }
  }
}

const updateCustomDisplayName = async (name) => {
  if (!user.value) {
    throw new Error('Must be logged in to update display name')
  }

  const trimmed = (name || '').trim()
  if (!trimmed) {
    throw new Error('Display name cannot be empty')
  }
  if (trimmed.length > 20) {
    throw new Error('Display name must be 20 characters or less')
  }

  error.value = null
  try {
    await updateDoc(doc(db, 'users', user.value.uid), {
      customDisplayName: trimmed
    })
    await fetchUserProfile(user.value.uid)
    return { success: true }
  } catch (err) {
    console.error('Error updating display name:', err)
    error.value = 'Failed to update display name. Please try again.'
    return { success: false, error: error.value }
  }
}

export function useAuth() {
  return {
    user,
    userProfile,
    isLoggedIn,
    isLoading,
    error,
    signInWithGoogle,
    signInWithApple,
    signInWithEmail,
    createAccountWithEmail,
    sendPasswordReset,
    signOut,
    updateCustomDisplayName
  }
}
